import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useAdminConnectorGeoFilters,
  useAdminHandsetLimits,
  type AdminConnectorGeoFilter,
  type AdminHandsetLimits,
} from "@/features/admin";
import { useState } from "react";
import { AdminPageLayout } from "../layout";
import { GeoFilterDialog } from "./components/geo-filter-dialog";
import { HandsetLimitsDialog } from "./components/handset-limits-dialog";

/**
 * Telephony limits — what a tenant's phones can cost **us**, across every
 * tenant.
 *
 * Five figures live on this page and only three of them are editable here, which
 * is the point: outbound authorisation, the prefix ceiling and the per-call
 * ceiling are ours; the monthly minute quota and the tenant's own prefix list
 * are theirs and are shown so the two can be read against each other. A ceiling
 * of "+33, +32" under a tenant list of "+33" is a client who does not want
 * Belgium — not something to fix.
 *
 * It exists as a cross-tenant page rather than a section of each tenant's
 * back-office because platform staff cannot enter a tenant's back-office at
 * all: those routes require an ACTIVE membership, and staff are members of
 * their own tenant only.
 */
export function AdminTelephonyPage() {
  const { data: handsets = [], isLoading } = useAdminHandsetLimits();
  const { data: connectors = [], isLoading: connectorsLoading } =
    useAdminConnectorGeoFilters();
  const [editing, setEditing] = useState<AdminHandsetLimits | null>(null);
  const [editingGeo, setEditingGeo] = useState<AdminConnectorGeoFilter | null>(
    null,
  );

  return (
    <AdminPageLayout
      title="Téléphonie"
      description="Ce que les postes et les connecteurs de chaque client peuvent nous coûter."
    >

      <section className="space-y-3">
        <div>
          <h3 className="text-lg font-semibold">Postes VoIP</h3>
          <p className="text-muted-foreground text-sm">
            Les appels sortants partent de notre trunk et arrivent sur notre
            facture : un poste est créé sans sortant, et c'est ici qu'il
            s'ouvre.
          </p>
        </div>

        {isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : handsets.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Aucun poste sur la plateforme.
          </p>
        ) : (
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Poste</TableHead>
                  <TableHead>Sortant</TableHead>
                  <TableHead>Préfixes autorisés</TableHead>
                  <TableHead>Choix du client</TableHead>
                  <TableHead>Durée max</TableHead>
                  <TableHead>Quota client</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {handsets.map((handset) => (
                  <TableRow key={`${handset.client_id}:${handset.id}`}>
                    <TableCell>{handset.client_name}</TableCell>
                    <TableCell>
                      <div>{handset.label}</div>
                      <div className="text-muted-foreground font-mono text-xs">
                        {handset.caller_id}
                      </div>
                    </TableCell>
                    <TableCell>
                      {handset.outbound_enabled ? (
                        <Badge>Autorisé</Badge>
                      ) : (
                        <Badge variant="outline">Coupé</Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {handset.max_prefixes.length
                        ? handset.max_prefixes.join(", ")
                        : "pays du numéro"}
                    </TableCell>
                    <TableCell className="text-muted-foreground font-mono text-xs">
                      {handset.allowed_prefixes.length
                        ? handset.allowed_prefixes.join(", ")
                        : "tous"}
                    </TableCell>
                    <TableCell>
                      {handset.max_call_seconds
                        ? `${handset.max_call_seconds} s`
                        : "1 h (défaut)"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {handset.monthly_minutes_cap
                        ? `${handset.monthly_minutes_cap} min`
                        : "illimité"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditing(handset)}
                      >
                        Régler
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      <section className="space-y-3">
        <div>
          <h3 className="text-lg font-semibold">Pays source des appels entrants</h3>
          <p className="text-muted-foreground text-sm">
            Chaque appel accepté nous coûte une jambe opérateur et un pipeline
            STT/LLM/TTS complet. Un filtre désactivé accepte le monde entier.
          </p>
        </div>

        {connectorsLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : connectors.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Aucun connecteur vocal.
          </p>
        ) : (
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Numéros</TableHead>
                  <TableHead>Filtre</TableHead>
                  <TableHead>Pays</TableHead>
                  <TableHead>Pays inconnu</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {connectors.map((connector) => (
                  <TableRow key={`${connector.client_id}:${connector.id}`}>
                    <TableCell>{connector.client_name}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {connector.phone_numbers.join(", ") || "—"}
                    </TableCell>
                    <TableCell>
                      {connector.geo_filter?.enabled ? (
                        <Badge>
                          {connector.geo_filter.mode === "allow"
                            ? "Liste blanche"
                            : "Liste noire"}
                        </Badge>
                      ) : (
                        <Badge variant="destructive">Aucun</Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {connector.geo_filter?.enabled
                        ? connector.geo_filter.countries.join(", ")
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {connector.geo_filter?.enabled
                        ? connector.geo_filter.allow_unknown
                          ? "accepté"
                          : "refusé"
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingGeo(connector)}
                      >
                        Régler
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      <HandsetLimitsDialog handset={editing} onClose={() => setEditing(null)} />
      <GeoFilterDialog
        connector={editingGeo}
        onClose={() => setEditingGeo(null)}
      />
    </AdminPageLayout>
  );
}
